import { ActivityType, ChangeSetStatus, ChangeSetType, DocumentType, EventType } from '~/db_types';
import { AssemblyTemplateType, CosmicDocumentStatus, DeliveryOrderLineItemStatus, DeliveryOrderLineItemType, DigitalLibraryRecordType, TpsDocumentType, UserRole } from '~/db_types/swagger_types';
const FileContentTypeIcons = {
    // Media
    image: 'fad fa-image',
    audio: 'fad fa-file-audio',
    video: 'fad fa-file-video',
    // Documents
    'application/pdf': 'fad fa-file-pdf',
    'application/msword': 'fad fa-file-word',
    'application/vnd.ms-word': 'fad fa-file-word',
    'application/vnd.oasis.opendocument.text': 'fad fa-file-word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml': 'fad fa-file-word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'fad fa-file-word',
    'application/vnd.ms-excel': 'fad fa-file-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml': 'fad fa-file-excel',
    'application/vnd.oasis.opendocument.spreadsheet': 'fad fa-file-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'fad fa-file-excel',
    'application/vnd.ms-powerpoint': 'fad fa-file-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml': 'fad fa-file-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'fad fa-file-powerpoint',
    'application/vnd.oasis.opendocument.presentation': 'fad fa-file-powerpoint',
    'text/plain': 'fad fa-file-alt',
    'text/html': 'fad fa-file-code',
    'application/json': 'fad fa-file-code',
    // Archives
    'application/gzip': 'fad fa-file-archive',
    'application/zip': 'fad fa-file-archive',
    'application/octet-stream': 'fad fa-file',
    default: 'fad fa-file'
};
export const getIconByContentType = (contentType) => {
    if (!contentType) {
        return CosmicIcons.help;
    }
    if (contentType.startsWith('image') || contentType.startsWith('video')) {
        const type = contentType.split('/')[0];
        if (type && type in CosmicIcons) {
            return CosmicIcons[type];
        }
    }
    if (contentType in CosmicIcons) {
        return CosmicIcons[contentType];
    }
    return FileContentTypeIcons.default;
};
// #region Icons that a frequently used
// this is not intended to be exported
const ACTION_ICON = 'fad fa-star-exclamation';
const ASTRONAUT_ICON = 'fa-duotone fa-user-astronaut';
const ATOM_ICON = 'fad fa-atom-alt';
const BAN_ICON = 'fa-solid fa-ban';
const CALIBRATION_ICON = 'fa-duotone fa-solid fa-bullseye-arrow';
const CIRCLE_WITH_CHECKMARK_ICON = 'fa-duotone fa-solid fa-check-circle';
const CIRCLE_WITH_X_ICON = 'fad fa-times-circle';
const COMPLETE_ICON = 'fa-solid fa-check';
const CLOSED_DOOR_ICON = 'fa-duotone fa-solid fa-door-closed';
const CYCLE_ARROWS_ICON = 'fa-solid fa-arrows-spin';
const GHOST_ICON = 'fa-duotone fa-ghost';
const INSPECTION_ICON = 'fa-regular fa-eyes';
const MAP_DUO_ICON = 'fad fa-sitemap';
const MAP_ICON = 'far fa-sitemap';
const OFFLINE_ICON = 'fa-duotone fa-solid fa-wifi-slash';
const OPEN_DOOR_ICON = 'fad fa-door-open';
const OTHER_ICON = 'fa-duotone fa-solid fa-block-question';
const SCALES_ICON = 'fa-duotone fa-solid fa-balance-scale';
const SEARCH_ICON = 'far fa-search';
const TASKS_ICON = 'fa-light fa-tasks';
const THUMBS_UP_ICON = 'fa-duotone fa-solid fa-thumbs-up';
const THUMBS_DOWN_ICON = 'fa-duotone fa-solid fa-thumbs-down';
// #endregion
export const CosmicIcons = {
    ...FileContentTypeIcons,
    // #region Activity Types
    [ActivityType.AUTOCYCLE]: CYCLE_ARROWS_ICON,
    [ActivityType.BATTERY_MAINTENANCE]: 'fad fa-car-battery',
    [ActivityType.CALIBRATION]: CALIBRATION_ICON,
    [ActivityType.CHARGE]: 'fad fa-battery-bolt',
    [ActivityType.CHECK_OUT]: ATOM_ICON,
    [ActivityType.CYCLE]: CYCLE_ARROWS_ICON,
    [ActivityType.DISCHARGE]: ATOM_ICON,
    [ActivityType.DUMP]: 'fa-duotone fa-dumpster',
    [ActivityType.DRY_OUT]: 'fa-duotone fa-cactus',
    [ActivityType.EMU_H20_DUMP_AND_FILL]: ATOM_ICON,
    [ActivityType.EMU_H20_RECHARGE]: ATOM_ICON,
    [ActivityType.EVA_ACTIVITY]: ATOM_ICON,
    [ActivityType.FILL]: 'fad fa-fill',
    [ActivityType.FLIGHT]: 'fa-duotone fa-plane-up',
    [ActivityType.GSE_CALIBRATION]: CALIBRATION_ICON,
    [ActivityType.GREASE_AND_CIRCULATE]: ATOM_ICON,
    [ActivityType.GMIP]: INSPECTION_ICON,
    [ActivityType.IODINATE_LOOP_SCRUB]: ATOM_ICON,
    [ActivityType.ITEM_146_CYCLE]: CYCLE_ARROWS_ICON,
    [ActivityType.INTERVAL]: CYCLE_ARROWS_ICON,
    [ActivityType.INSPECTION]: INSPECTION_ICON,
    [ActivityType.MIP]: INSPECTION_ICON,
    [ActivityType.PRESSURIZATION]: 'fa-duotone fa-gem',
    [ActivityType.O2_POPPET_VERIFICATION]: ATOM_ICON,
    [ActivityType.OPERATION]: 'fa-duotone fa-scalpel-line-dashed',
    [ActivityType.OTHER]: ATOM_ICON,
    [ActivityType.SERVICE]: 'fa-duotone fa-car-wrench',
    [ActivityType.RECEIVING_INSPECTION]: INSPECTION_ICON,
    [ActivityType.REGENERATION]: ATOM_ICON,
    [ActivityType.RESIZE_OR_DRY_RUN]: ATOM_ICON,
    [ActivityType.TAK_DATA_COLLECTION]: ATOM_ICON,
    [ActivityType.TEN_POINT_TWO_OPS]: ATOM_ICON,
    [ActivityType.TEST]: ATOM_ICON,
    [ActivityType.TETHER_INSPECTION]: INSPECTION_ICON,
    [ActivityType.VENT]: ATOM_ICON,
    [ActivityType.WLVTA_REPLACEMENT]: ATOM_ICON,
    [ActivityType.WETTING_OR_IODINATING]: 'fa-duotone fa-droplet',
    // #endregion
    // #region AssemblyTemplateTypes
    [AssemblyTemplateType.MEGA]: ASTRONAUT_ICON,
    [AssemblyTemplateType.ASSEMBLY_TEMPLATE]: MAP_ICON,
    [AssemblyTemplateType.EVENT_ASSEMBLY]: MAP_ICON,
    [AssemblyTemplateType.PRODUCT_BREAKDOWN_STRUCTURE]: MAP_DUO_ICON,
    [AssemblyTemplateType.SEMU]: ASTRONAUT_ICON,
    [AssemblyTemplateType.SUPER]: ASTRONAUT_ICON,
    // #endregion
    // #region Booleans
    true: CIRCLE_WITH_CHECKMARK_ICON,
    false: BAN_ICON,
    // #endregion
    // #region CosmicDocumentStatus
    [CosmicDocumentStatus.AWAITING_CLOSURE_APPROVAL]: 'fa-duotone fa-solid fa-hourglass-clock',
    [CosmicDocumentStatus.BLOCKED]: 'fa-duotone fa-solid fa-do-not-enter',
    [CosmicDocumentStatus.CANCELLED]: BAN_ICON,
    [CosmicDocumentStatus.CLOSED]: CLOSED_DOOR_ICON,
    [CosmicDocumentStatus.CLOSED_INCOMPLETE]: CLOSED_DOOR_ICON,
    [CosmicDocumentStatus.DRAFT]: 'fa-duotone fa-drafting-compass',
    // [CosmicDocumentStatus.OPEN]: OPEN_DOOR_ICON, // already defined below
    [CosmicDocumentStatus.PENDING]: 'fad fa-spinner',
    // [CosmicDocumentStatus.REJECTED]: CIRCLE_WITH_X_ICON, // already defined below
    [CosmicDocumentStatus.WORK_ORDER_STEP_REJECTED]: BAN_ICON,
    [CosmicDocumentStatus.WORK_ORDER_STEPS_COMPLETE]: 'fad fa-clipboard-check',
    [CosmicDocumentStatus.WRITTEN_IN_ERROR]: BAN_ICON,
    [CosmicDocumentStatus.REMEDIAL_ACTIONS_COMPLETE]: COMPLETE_ICON,
    // #endregion
    // #region ChangeSetStatus
    [ChangeSetStatus.APPROVED]: THUMBS_UP_ICON,
    [ChangeSetStatus.REJECTED]: THUMBS_DOWN_ICON,
    [ChangeSetStatus.OPEN]: OPEN_DOOR_ICON,
    // #endregion
    // #region ChangeSetType
    [ChangeSetType.COSMIC_ACTION]: ACTION_ICON,
    [ChangeSetType.ITEM_INSTANCE_ON_ORBIT_FAILURE]: 'fad fa-planet-moon',
    // #endregion
    // #region cTPS specific
    tps_warning: 'fa-duotone fa-skull-crossbones',
    tps_caution: 'fa-duotone fa-triangle-exclamation',
    //#endregion
    // #region DeliveryOrderLineItemType
    [DeliveryOrderLineItemType.FL]: 'fa-solid fa-stars',
    [DeliveryOrderLineItemType.NEW_BUILD]: 'fa-solid fa-trowel-bricks',
    [DeliveryOrderLineItemType.PROCURE]: 'fa-solid fa-money-bill-wave',
    [DeliveryOrderLineItemType.REFURB]: 'fa-solid fa-recycle',
    // #endregion
    // #region DeliveryOrderLineItemStatus
    [DeliveryOrderLineItemStatus.IN_PROCESS]: 'fa-duotone fa-solid fa-arrow-progress',
    [DeliveryOrderLineItemStatus.NOT_STARTED]: 'fa-solid fa-egg',
    [DeliveryOrderLineItemStatus.PROBABLE]: 'fa-duotone fa-solid fa-sunrise',
    [DeliveryOrderLineItemStatus.PROPOSAL]: 'fa-duotone fa-solid fa-ring-diamond',
    [DeliveryOrderLineItemStatus.READY_FOR_COMPLETION]: COMPLETE_ICON,
    [DeliveryOrderLineItemStatus.VOID]: 'fa-solid fa-circle',
    // #endregion
    // #region DigitalLibraryRecordType
    [DigitalLibraryRecordType.ARCHITECTURE]: 'fad fa-archway',
    [DigitalLibraryRecordType.CERTIFICATION]: 'fad fa-file-certificate',
    [DigitalLibraryRecordType.TECHNICAL_DOCUMENTATION]: 'fad fa-university',
    [DigitalLibraryRecordType.VERIFICATION]: 'fad fa-badge-check',
    // #endregion
    // #region Document Types
    [DocumentType.C_NONCONFORMANCE_REPORT]: 'fal fa-fragile',
    [DocumentType.C_EASY_TRANSFER]: 'fad fa-shipping-fast',
    [DocumentType.C_FAILURE_REPORTING_ANALYSIS_CORRECTIVE_ACTION_SYSTEM]: 'fad fa-bullhorn',
    [DocumentType.GCAR]: 'fa-duotone fa-solid fa-file-certificate',
    [DocumentType.C_RCA]: 'fad fa-comment-exclamation',
    [DocumentType.C_TPS]: TASKS_ICON,
    [DocumentType.C_WORKFLOW]: 'fad fa-chart-network',
    [DocumentType.C_DIGITAL_RECORD_REVIEW_ITEM]: 'fal fa-highlighter',
    [DocumentType.C_PROCEDURE]: 'fa-duotone fa-solid fa-list-ol',
    [DocumentType.ACCEPTANCE_DATA_PACK]: 'fad fa-hand-holding-box',
    [DocumentType.DRAWING]: 'fas fa-drafting-compass',
    [DocumentType.DISCREPANCY_REPORT]: 'fal fa-fragile',
    [DocumentType.DELIVERY_ORDER]: 'fa-regular fa-file-invoice-dollar',
    [DocumentType.ENGINEERING_CHANGE]: 'fad fa-random',
    [DocumentType.EASY_TRANSFER]: 'fal fa-shipping-fast',
    [DocumentType.FAILURE_INVESTIGATION_ANOMALY_REPORT]: 'fad fa-fire',
    [DocumentType.MATERIAL_USAGE_AGREEMENT]: 'fas fa-handshake',
    [DocumentType.PROCEDURE]: 'fad fa-shoe-prints',
    [DocumentType.OTHER_DOCUMENT]: OTHER_ICON,
    [DocumentType.OVER_DUE_TAG]: 'fad fa-tag',
    [DocumentType.TPS]: TASKS_ICON,
    // [DocumentType.WAIVER]: 'fa-regular fa-wave-square', // already defined above
    [DocumentType.YELLOW_TAG_NOTICE]: 'fad fa-tag',
    // #endregion
    // #region EntityTypes
    ['activities']: 'fad fa-stopwatch',
    ['approval_routing_templates']: 'fad fa-route',
    ['assembly_projections']: 'fa-duotone fa-question',
    ['assembly_templates']: MAP_ICON,
    ['assembly_transactions']: 'fal fa-receipt',
    ['assemblies']: MAP_DUO_ICON,
    ['change_sets']: 'fad fa-code-merge',
    ['comments']: 'fad fa-comment-alt-lines',
    ['contacts']: 'fad fa-address-book',
    ['contracts']: 'fad fa-file-contract',
    ['crews']: 'fad fa-user-astronaut',
    ['delivery_matrix_items']: 'fad fa-parachute-box',
    ['deliverables']: 'fad fa-parachute-box',
    ['digital_records']: 'fad fa-record-vinyl',
    ['documents']: 'fad fa-file-alt',
    ['events']: 'fad fa-calendar-alt',
    ['hardware_list_templates']: 'far fa-clipboard-list',
    ['hardware_lists']: 'fad fa-clipboard-list-check',
    ['inventories']: 'fa-duotone fa-solid fa-grid',
    ['item_drawings']: 'fa-duotone fa-solid fa-square',
    ['item_instances']: 'fa-solid fa-grid',
    ['item_masters']: 'fa-duotone fa-grid-2',
    ['jf288']: 'fad fa-user-hard-hat',
    ['locations']: 'fad fa-archive',
    ['measurements']: 'fas fa-ruler-combined',
    ['organizations']: 'fad fa-building',
    ['physical_inventory_audits']: 'fad fa-tally',
    ['product_breakdown_structures']: MAP_DUO_ICON,
    ['responsible_organizations']: 'fad fa-landmark',
    ['risks']: 'fad fa-umbrella',
    ['transactions']: 'fal fa-receipt',
    ['user_groups']: 'fad fa-users',
    ['users']: 'fad fa-user',
    ['vehicles']: 'fad fa-rocket',
    ['working_assemblies']: MAP_DUO_ICON,
    // #endregion
    // #region EventType
    [EventType.ALLOCATION_WINDOW]: 'fa-duotone fa-window-frame-open',
    [EventType.DIGITAL_RECORD_REVIEW]: 'fad fa-album-collection',
    [EventType.DISPOSAL]: 'fa-duotone fa-fire-flame',
    [EventType.GENERAL]: 'fad fa-calendar-alt',
    [EventType.LAUNCH]: 'fa-duotone fa-plane-departure',
    [EventType.MISHAP]: 'fa-regular fa-person-falling-burst',
    [EventType.RETURN]: 'fa-duotone fa-plane-arrival',
    [EventType.TRAINING]: 'fa-regular fa-dumbbell',
    [EventType.WAIVER]: 'fa-regular fa-wave-square',
    // #endregion
    // #region MegaAllocationTypes (cant include type bc it only exists in logistics for now)
    ['GROUND']: 'fa-regular fa-person-arrow-down-to-line',
    ['ON_ORBIT']: 'fa-regular fa-planet-moon',
    ['TRADE_STUDY']: 'fa-regular fa-face-monocle',
    ['SPARE']: 'fad fa-solid fa-donut',
    ['LON']: 'fad fa-solid fa-plane-departure',
    // #endregion
    // #region TpsDocumentType
    [TpsDocumentType.ACCEPTANCE_TEST]: 'fa-duotone fa-check-double',
    [TpsDocumentType.ACTIVATION_INACTIVATION_OF_HARDWARE]: GHOST_ICON,
    [TpsDocumentType.ASSEMBLY]: MAP_DUO_ICON,
    [TpsDocumentType.CLASSIFICATION]: TASKS_ICON,
    [TpsDocumentType.CONFIGURATION_CHANGE]: TASKS_ICON,
    [TpsDocumentType.DOWNGRADE_NON_DISCREPANT]: 'fa-duotone fa-down-to-line',
    [TpsDocumentType.ENGINEERING_EVALUATION]: 'fa-duotone fa-users-gear',
    [TpsDocumentType.FABRICATION]: TASKS_ICON,
    [TpsDocumentType.FABRICATION_I_E]: TASKS_ICON,
    [TpsDocumentType.I_E_PROJECT_WORK]: TASKS_ICON,
    [TpsDocumentType.SIX_ONE_ONE]: TASKS_ICON,
    [TpsDocumentType.MAINTENANCE]: 'fa-duotone fa-solid fa-screwdriver-wrench',
    [TpsDocumentType.OTHER_CONFIGURATION_CHANGE]: TASKS_ICON,
    [TpsDocumentType.OTHER_NON_CONFIGURATION_CHANGE]: OTHER_ICON,
    [TpsDocumentType.POST_FLIGHT_ACTIVITY]: 'fa-duotone fa-solid fa-plane-circle-check',
    [TpsDocumentType.QUALIFICATION_CERTIFICATION_TEST]: 'fad fa-file-certificate',
    [TpsDocumentType.TEMPORARY_CONFIGURATION_CHANGE]: TASKS_ICON,
    [TpsDocumentType.TEST_CALIBRATION]: CALIBRATION_ICON,
    [TpsDocumentType.TRANSFER_ROUND_ROBIN]: TASKS_ICON,
    // #endregion
    // #region UserRole
    [UserRole.ADMIN]: 'fad fa-user-crown',
    [UserRole.DISABLED]: 'fad fa-user-slash',
    [UserRole.USER]: 'fad fa-user',
    [UserRole.SYSTEM]: 'fad fa-solar-system',
    // #endregion
    // #region Vuetify Internal Icons
    // Required by Vuetify components (data table pagination, dropdowns, checkboxes, etc.)
    cancel: CIRCLE_WITH_X_ICON,
    checkboxIndeterminate: 'fas fa-minus-square',
    checkboxOff: 'far fa-square',
    checkboxOn: 'fas fa-check-square',
    delete: 'fad fa-trash-alt',
    delimiter: 'fas fa-circle',
    dropdown: 'fas fa-caret-down',
    error: 'fad fa-exclamation-circle',
    file: 'fas fa-paperclip',
    first: 'fas fa-step-backward',
    last: 'fas fa-step-forward',
    menu: 'fas fa-bars',
    next: 'fas fa-chevron-right',
    prev: 'fas fa-chevron-left',
    radioOff: 'far fa-circle',
    radioOn: 'fas fa-dot-circle',
    ratingEmpty: 'far fa-star',
    ratingFull: 'fas fa-star',
    ratingHalf: 'fas fa-star-half-alt',
    subgroup: 'fas fa-caret-down',
    success: CIRCLE_WITH_CHECKMARK_ICON,
    unfold: 'fas fa-arrows-alt-v',
    // #endregion
    actions: 'fa-duotone fa-solid fa-person-running',
    ACTUALIZATION: 'fad fa-hat-wizard',
    administrate: 'fad fa-clipboard-list',
    advanced: 'fad fa-robot',
    approve: THUMBS_UP_ICON,
    alias: 'fad fa-user-secret',
    angle_left: 'fas fa-angle-left',
    angle_right: 'fas fa-angle-right',
    application_planning: 'fad fa-calendar-day',
    application_reporting: 'fad fa-chart-line',
    archived: GHOST_ICON,
    arrow_down: 'fad fa-long-arrow-alt-down',
    arrow_left: 'fad fa-long-arrow-alt-left',
    arrow_right: 'fad fa-long-arrow-alt-right',
    arrow_up: 'fad fa-long-arrow-alt-up',
    attachment: 'fad fa-folder-open',
    AUTHORITY: 'fad fa-gavel',
    bolt: 'fad fa-bolt',
    browser: 'fad fa-browser',
    calendar_day: 'fad fa-calendar-day',
    calendar: 'fad fa-calendar',
    checklist: 'fad fa-clipboard-list-check',
    chevron_down: 'fa fa-chevron-down',
    chevron_up: 'fa fa-chevron-up',
    circle_check: CIRCLE_WITH_CHECKMARK_ICON,
    circle_plus: 'fad fa-plus-circle',
    circle: 'fad fa-circle',
    clear: CIRCLE_WITH_X_ICON,
    clone: 'fad fa-clone',
    close: CIRCLE_WITH_X_ICON,
    cog: 'fad fa-cog',
    comparison: 'fad fa-columns',
    complete: COMPLETE_ICON,
    components_isTool: 'fad fa-wrench',
    cost: 'fad fa-dollar-sign',
    create_fat: 'fas fa-plus',
    create: 'fal fa-plus',
    crews_female: 'fad fa-female',
    crews_male: 'fad fa-male',
    CRITICAL_INVENTORY_TRACKING: 'fad fa-dice-d20',
    demo: 'fad fa-cog fa-spin',
    document_approval: SCALES_ICON,
    download: 'fad fa-cloud-download',
    edit: 'fa-duotone fa-solid fa-pen-to-square',
    ellipsis: 'fas fa-ellipsis-v',
    // event: 'fad fa-calendar-day',
    exclamation: 'fad fa-exclamation',
    expand: 'fas fa-angle-up',
    external_link: 'fad fa-external-link',
    eye_slash: 'fas fa-eye-slash',
    eye: 'far fa-eye',
    filter: 'fas fa-sm fa-filter',
    group: 'fal fa-object-group',
    help: 'fad fa-question-circle',
    history: 'fa-duotone fa-light fa-dinosaur',
    home: 'fad fa-home',
    import: 'fad fa-file-import',
    info_circle: 'fad fa-info-circle',
    info_strong: 'fas fa-info',
    info: 'fal fa-info',
    install: 'fas fa-download',
    lil_edit: 'fa-light fa-pencil caption',
    limited_life: 'fad fa-sensor-alert',
    link: 'fad fa-link',
    loading: 'fad fa-spinner fa-spin',
    mail: 'fad fa-mail-bulk',
    mip: INSPECTION_ICON,
    MANAGE_PAGE: 'fad fa-wrench',
    metadata: 'fad fa-binary',
    milestones: 'fal fa-medal',
    mini: 'fa-duotone fa-solid fa-rectangle-wide',
    minimize: 'fas fa-window-minimize',
    movement: 'fad fa-shipping-fast',
    need: 'fa-regular fa-hand-holding-box',
    notes: 'fa-duotone fa-notes',
    objectGroup: 'fal fa-object-group',
    offline_mode: OFFLINE_ICON,
    offline: OFFLINE_ICON,
    other: OTHER_ICON,
    paperwork: 'fad fa-scroll',
    part_reference: 'fa-regular fa-grid',
    part_tool: 'fad fa-wrench',
    pdf: 'fad fa-file-pdf',
    pipeline_approved: CIRCLE_WITH_CHECKMARK_ICON,
    pipeline_canceled: 'fal fa-ban',
    pipeline_complete: 'fad fa-clipboard-check',
    pipeline_connector: 'fas fa-arrow-right',
    pipeline_null: 'fad fa-minus-circle',
    pipeline_open: 'fas fa-circle-notch',
    pipeline_rejected: CIRCLE_WITH_X_ICON,
    play: 'fas fa-play',
    pause: 'fas fa-pause',
    print_slash: 'fad fa-print-slash',
    print: 'fa fa-print',
    project_codes: 'fa-duotone fa-solid fa-money-check-dollar-pen',
    refresh: 'fad fa-sync',
    reject: THUMBS_DOWN_ICON,
    relationships: 'fad fa-spider-web',
    reload: 'fad fa-sync',
    reopen: 'fad fa-redo-alt',
    repeat: 'fad fa-repeat',
    report_bar: 'fad fa-chart-bar',
    report: 'fad fa-user-chart',
    revive: 'fa-duotone fa-heart-circle-bolt',
    root: 'fa-duotone fa-solid fa-crown',
    save: 'fad fa-save',
    save_all: 'fa-duotone fa-solid fa-floppy-disks',
    scanner: 'far fa-scanner',
    search: SEARCH_ICON,
    select_all_off: 'fal fa-square',
    select_all: 'fad fa-check-square',
    sort: 'fad fa-long-arrow-up',
    source_document_true: 'fa-duotone fa-solid fa-outlet',
    source_document_false: 'fa-thin fa-outlet',
    square_check: 'fad fa-check-square',
    star: 'fas fa-star',
    status_close: CLOSED_DOOR_ICON,
    status_open: OPEN_DOOR_ICON,
    steps: 'fa-duotone fa-solid fa-stairs',
    stop: 'fas fa-stop',
    strong_close: CIRCLE_WITH_X_ICON,
    support_ticket: 'fad fa-solid fa-tickets',
    table: 'fad fa-table',
    // transaction: 'fal fa-receipt',
    trash: 'fad fa-trash-alt',
    unarchive: 'fas fa-heartbeat',
    uninstall: 'fas fa-upload',
    user_hat: 'fad fa-user-hard-hat',
    warning: 'fad fa-exclamation-triangle',
    work_order_step: 'fad fa-clipboard'
};
